import { createContext } from "react";
import type { AccessQuery } from "./core/access";

export interface IUserPayload<
    Role extends string = string,
    Permission extends string = string
> {
    id: string | number;
    roles?: Role[];
    permissions?: Permission[];
}

export interface PermissionAuthContext {
    /** The currently set user, or `null` when no user is set. */
    user: IUserPayload | null;
    /** Sets (or replaces) the current user. */
    setUser: (user: IUserPayload) => void;
    /** Clears the current user (e.g. on logout). */
    clearUser: () => void;
    /** Synchronous authorization check. */
    can: (query: AccessQuery) => boolean;
    /**
     * Promise-based authorization check. Kept for backwards compatibility;
     * resolves synchronously since checks are performed in memory.
     */
    isAuthorized: (
        roleNames?: string[] | string,
        permissionNames?: string[] | string,
        options?: Pick<AccessQuery, "requireAll" | "check">
    ) => Promise<boolean>;
    /** Retained for backwards compatibility; always `false` (checks are instant). */
    isLoading: boolean;
}

const notInitialized = (): never => {
    throw new Error(
        "PermissionProvider is missing. Wrap your application with <PermissionProvider> before using permission features."
    );
};

const PermissionContext = createContext<PermissionAuthContext>({
    user: null,
    setUser: notInitialized,
    clearUser: notInitialized,
    can: notInitialized,
    isAuthorized: notInitialized,
    isLoading: false,
});

export default PermissionContext;